import { Button, Label, Switch } from '@playcastdotio/Shadcn';
import { PlaycastReactFunctions } from '../PlaycastWrapper';
import { generateQRCode } from '@playcastdotio/Qrcode';
import pcLogo from '../../../assets/playcast_logo-96x96.png';
import { useEffect, useState, useCallback } from 'react';
import constants from '@playcastdotio/GlobalConfig';

export const Overlays = ({
  context,
  handleQrCodeSwitch,
}: {
  context: PlaycastReactFunctions;
  handleQrCodeSwitch: (showStreamQrCode: boolean) => void;
}) => {
  const [svgUrl, setSvgUrl] = useState<string | null>(null);
  const [browserSourceUrl, setBrowserSourceUrl] = useState<string>('Loading URL...');
  const [streamUrl, setStreamUrl] = useState<string>('');
  const [isCopied, setIsCopied] = useState(false);
  const [isQrCodeEnabled, setIsQrCodeEnabled] = useState(context.stateStores.hostState.showStreamQrCode);

  const getObsBrowserSourceUrl = useCallback(async (): Promise<string> => {
    const machineId = context.stateStores.machineIdState;

    // Generate hash as a buffer
    const hashBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(machineId));

    // Convert to Base64 and make URL-safe
    const base64Hash = btoa(String.fromCharCode(...new Uint8Array(hashBuffer))).replace(/[+/=]/g, '');

    // Truncate to 10 characters for a shorter QR code
    const shortHash = base64Hash.substring(0, 12);

    const branchName = constants.VERSION.split('-')[1];
    if (!branchName) {
      return `https://playcast.io/qr/${shortHash}`;
    }
    if (branchName === 'local') {
      return `http://localhost:4204/qr/${shortHash}`;
    }
    if (branchName === 'dev') {
      return `https://dev.marketing.playcast.io/qr/${shortHash}`;
    }    
    if (branchName === 'dev2') {
      return `https://dev.marketing.playcast.io/qr/${shortHash}`;
    }
    if (branchName === 'prerelease') {
      return `https://prerelease.marketing.playcast.io/qr/${shortHash}`;
    }
    return `https://${branchName}.marketing.playcast.io/qr/${shortHash}`;
  }, [context.stateStores.machineIdState]);

  useEffect(() => {
    const streamUrl = `${context.stateStores.invitationStateV2.type === 'cafe' ? constants.PAVILION_PLAYER_BASE_URL : constants.PRIME_PLAYER_BASE_URL}j/${context.stateStores.invitationStateV2.id}`;
    setStreamUrl(streamUrl);
  }, [context.stateStores.invitationStateV2.id, context.stateStores.invitationStateV2.type]);

  useEffect(() => {
    if (isCopied) {
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    }
  }, [isCopied]);

  useEffect(() => {
    const generateQr = async () => {
      // const url = await getObsBrowserSourceUrl();
      generateQRCode(streamUrl, { logo: pcLogo })
        .then((svgString: string | null | undefined) => {
          if (svgString) {
            setSvgUrl(svgString);
          } else {
            console.error('Failed to generate QR code: No SVG string returned');
          }
        })
        .catch((error) => {
          console.error('Failed to generate QR code:', error);
        });
    };

    generateQr();
  }, [streamUrl]);

  useEffect(() => {
    // Load the browser source URL on component mount
    const loadBrowserSourceUrl = async () => {
      const url = await getObsBrowserSourceUrl();
      setBrowserSourceUrl(url);
    };

    loadBrowserSourceUrl();
  }, [context.stateStores.machineIdState]); // Re-run when machineId changes

  const handleDownloadQrImage = () => {
    if (!svgUrl) {
      console.error('Failed to generate QR code: No SVG URL');
      return;
    }

    const qrCodeImage = new Image();
    qrCodeImage.src = svgUrl;
    qrCodeImage.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = qrCodeImage.width;
      canvas.height = qrCodeImage.height;
      const context = canvas.getContext('2d');
      if (context) {
        context.drawImage(qrCodeImage, 0, 0);
        const qrCodeImageUrl = canvas.toDataURL('image/png');
        const a = document.createElement('a');
        a.href = qrCodeImageUrl;
        a.download = `playcast-qr-${Date.now()}.png`;
        a.click();
      }
    };
  };

  const handleCopyBrowserSourceUrl = async () => {
    try {
      const url = await getObsBrowserSourceUrl();
      await navigator.clipboard.writeText(url);
      setIsCopied(true);
    } catch (error) {
      console.error('Failed to copy URL to clipboard:', error);
    }
  };

  const handleQrCodeSwitchFunc = async () => {
    setIsQrCodeEnabled(!isQrCodeEnabled);
    handleQrCodeSwitch(isQrCodeEnabled);
  };

  if (!svgUrl) {
    return (
      <div data-testid='content-root' className='flex flex-1 flex-col gap-4 text-white'>
        <Label className='font-[Syne] text-3xl'>QR Code</Label>
        <div data-testid='content-children-container' className='flex h-full w-full flex-col gap-4'>
          <div className='text-sm text-gray-400'>Generating QR code...</div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid='content-root' className='flex flex-1 flex-col gap-4 text-white'>
      <Label className='font-[Syne] text-3xl'>QR Code</Label>
      <div className='flex flex-col gap-4 rounded-lg border border-gray-500/75 px-2 py-2'>
        <div className='flex flex-col gap-2'>
          <Label className='font-[Syne] text-2xl'>OBS Browser Source URL</Label>
          <div className='flex w-full items-center gap-2'>
            <input
              type='text'
              readOnly
              value={browserSourceUrl}
              className='flex-1 rounded border border-gray-300 bg-gray-50 px-3 py-2 font-mono text-sm text-black focus:outline-none'
            />
            <Button
              className='whitespace-nowrap bg-amber-400 hover:bg-amber-400/60'
              onClick={handleCopyBrowserSourceUrl}
              data-testid='share-copy-link-button'
            >
              {isCopied ? 'Copied!' : 'Copy Link'}
            </Button>
          </div>
          <p className='text-xs text-gray-500'>Add this URL as a browser source in OBS to display the QR code.</p>
        </div>
      </div>
      {context.stateStores.hostState.status === 'streaming' && (
        <div data-testid='content-children-container' className='flex h-full w-full flex-col gap-4'>
          <div className='flex flex-col gap-4 rounded-lg border border-gray-500/75 px-2 py-2'>
            <div className='flex items-center gap-2'>
              <Switch
                id='qr-code-switch'
                checked={isQrCodeEnabled}
                onCheckedChange={handleQrCodeSwitchFunc}
                className='data-[state=checked]:bg-emerald-400 data-[state=unchecked]:bg-gray-700 [&>span]:bg-white'
              />
              <Label htmlFor='qr-code-switch' className='text-md cursor-pointer'>
                QR Code inside playcast
              </Label>
            </div>
            <div className='ml-4 w-fit border border-white bg-black p-2 text-xs text-black'>
              <img src={svgUrl} alt='QR code' className='size-40' />
            </div>
            <Button className='m-0 w-fit bg-amber-400 hover:bg-amber-400/60' onClick={handleDownloadQrImage}>
              Download QR code for current stream
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
